import React, { ReactNode, useCallback, useMemo } from 'react';
import { ThreeEvent } from '@react-three/fiber';

import { CalculatedLine, Component } from '@paragon/contract-library';
import { calculateLine } from '@paragon/relative-geometry';
import { RaycastOrder } from '@paragon/three-shared';
import { allPairs, notNullish } from '@paragon/utilities';

import { ReferenceLineIntersection } from '../../store/slices/applicationSlice';
import { SnapVisual } from '../dimension/SnapVisual';
import { useMousePositionInScene } from '../hooks/useMousePositionInScene';
import { threeJsDom } from '../util/events/ThreeJSDom';
import { raycastReferenceLineContainer } from '../util/raycastReferenceLineContainer';
import { getClosestIntersection } from './getClosestIntersection';

interface Props {
  readonly component: Component;
  readonly visibleReferenceLines: readonly CalculatedLine[];
  readonly onClickIntersection: (intersection: ReferenceLineIntersection) => void;
  readonly children?: (hoveredIntersection: ReferenceLineIntersection | null) => ReactNode;
}

export function ReferenceLineIntersections({ component, visibleReferenceLines, onClickIntersection, children }: Props) {
  const mousePosition = useMousePositionInScene();

  const referenceLineIntersections = useMemo(() => {
    return allPairs(visibleReferenceLines)
      .map(([lineA, lineB]) => {
        const lineAGeometry = calculateLine(lineA, component);
        const lineBGeometry = calculateLine(lineB, component);
        if (!lineAGeometry || !lineBGeometry) return null;
        const intersectionResult = lineAGeometry.intersectWithLine(lineBGeometry);
        if (!intersectionResult) return null;
        return {
          line1Guid: lineA.guid,
          line2Guid: lineB.guid,
          point: intersectionResult,
        };
      })
      .filter(notNullish);
  }, [component, visibleReferenceLines]);

  const hoveredIntersection = mousePosition ? getClosestIntersection(mousePosition, referenceLineIntersections) : null;

  const onClick = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      if (!threeJsDom.shouldDispatchClickEvent()) return;

      const closestIntersection = getClosestIntersection(event.point, referenceLineIntersections);
      if (!closestIntersection) return;

      event.stopPropagation();
      onClickIntersection(closestIntersection);
    },
    [onClickIntersection, referenceLineIntersections],
  );

  return (
    <group
      onClick={onClick}
      raycast={raycastReferenceLineContainer}
      userData-raycastOrder={RaycastOrder.ReferenceLineIntersections}
    >
      {children ? children(hoveredIntersection) : null}
      {hoveredIntersection ? (
        <SnapVisual
          snapType="Intersection"
          size={0.8}
          position={[hoveredIntersection.point.x, hoveredIntersection.point.y, 0]}
        />
      ) : null}
    </group>
  );
}
